/**
 * computeNextAction — derives the single most important next action
 * from the current OperatorState.
 *
 * Rules are evaluated in strict priority order (doc 32 §6).
 * Returns the first matching NextAction object, or null if all green.
 *
 * NextAction shape: { key, messageKey, route, severity }
 *   - key:        machine-readable action id (NextActionKey)
 *   - messageKey: i18n message key for merchant-facing label
 *   - route:      symbolic route target (ActionRoute) — null for informational
 *   - severity:   'info' | 'warning' | 'error'
 */

import {
  ConnectionStatus,
  IndexingStatus,
  PreviewBuildStatus,
  PreviewBrowserStatus,
  LiveStatus,
  IntegrationHealth,
  NextActionKey,
  ActionRoute,
  MODE_SUPPORT,
} from './operatorState';

const RULES = [
  // 1
  {
    match: (s) => s.connection === ConnectionStatus.ERROR,
    key: NextActionKey.FIX_CONNECTION,
    messageKey: 'operator.next.fix_connection',
    route: ActionRoute.DIAGNOSTICS,
    severity: 'error',
  },
  // 2
  {
    match: (s) => s.connection === ConnectionStatus.NOT_CONNECTED,
    key: NextActionKey.CONNECT_STORE,
    messageKey: 'operator.next.connect_store',
    route: ActionRoute.WIZARD_WELCOME,
    severity: 'warning',
  },
  // 3
  {
    match: (s) => s.indexing === IndexingStatus.ERROR,
    key: NextActionKey.FIX_INDEXING,
    messageKey: 'operator.next.fix_indexing',
    route: ActionRoute.DIAGNOSTICS,
    severity: 'error',
  },
  // 4
  {
    match: (s) => s.indexing === IndexingStatus.IDLE,
    key: NextActionKey.START_INDEXING,
    messageKey: 'operator.next.start_indexing',
    route: ActionRoute.HOME_INDEX,
    severity: 'info',
  },
  // 5
  {
    match: (s) => s.indexing === IndexingStatus.RUNNING,
    key: NextActionKey.WAIT_INDEXING,
    messageKey: 'operator.next.wait_indexing',
    route: null,
    severity: 'info',
  },
  // 6
  {
    match: (s) => s.mode === null,
    key: NextActionKey.CHOOSE_MODE,
    messageKey: 'operator.next.choose_mode',
    route: ActionRoute.WIZARD_EXPERIENCE,
    severity: 'info',
  },
  // 7 — previewBuild error (uses .preview alias = previewBuild for backward compat)
  {
    match: (s) => s.preview === PreviewBuildStatus.ERROR,
    key: NextActionKey.FIX_PREVIEW_BUILD,
    messageKey: 'operator.next.fix_preview_build',
    route: ActionRoute.DIAGNOSTICS,
    severity: 'error',
  },
  // 8
  {
    match: (s) => s.preview === PreviewBuildStatus.NOT_READY,
    key: NextActionKey.BUILD_PREVIEW,
    messageKey: 'operator.next.build_preview',
    route: ActionRoute.WIZARD_BUILD,
    severity: 'info',
  },
  // 9
  {
    match: (s) => s.preview === PreviewBuildStatus.BUILDING,
    key: NextActionKey.WAIT_PREVIEW_BUILD,
    messageKey: 'operator.next.wait_preview_build',
    route: null,
    severity: 'info',
  },
  // 10 — previewBrowser error
  {
    match: (s) => s.previewBrowser === PreviewBrowserStatus.ERROR,
    key: NextActionKey.CHECK_PREVIEW,
    messageKey: 'operator.next.check_preview',
    route: ActionRoute.DIAGNOSTICS,
    severity: 'error',
  },
  // 11 — stale preview feedback is informational inside Diagnostics only.
  // It must not become a persistent Home next-action because localStorage
  // feedback naturally goes stale when the storefront preview tab is closed.
  // 12
  {
    match: (s) => s.integrationHealth === IntegrationHealth.ERROR,
    key: NextActionKey.FIX_INTEGRATION,
    messageKey: 'operator.next.fix_integration',
    route: ActionRoute.INTEGRATION_SETTINGS,
    severity: 'error',
  },
  // 13
  {
    match: (s) => s.live === LiveStatus.ERROR,
    key: NextActionKey.FIX_LIVE,
    messageKey: 'operator.next.fix_live',
    route: ActionRoute.DIAGNOSTICS,
    severity: 'error',
  },
  // 14
  {
    match: (s) => s.live === LiveStatus.NOT_ACTIVE && s.preview !== PreviewBuildStatus.UNKNOWN,
    key: NextActionKey.PUBLISH,
    messageKey: 'operator.next.publish',
    route: ActionRoute.HOME_PUBLISH,
    severity: 'info',
  },
  // 15
  {
    match: (s) => s.integrationHealth === IntegrationHealth.WARNING,
    key: NextActionKey.REVIEW_INTEGRATION,
    messageKey: 'operator.next.review_integration',
    route: ActionRoute.INTEGRATION_SETTINGS,
    severity: 'warning',
  },
];

export function computeNextAction(state) {
  for (const rule of RULES) {
    if (rule.match(state)) {
      return {
        key: rule.key,
        messageKey: rule.messageKey,
        route: rule.route,
        severity: rule.severity,
      };
    }
  }
  return null;
}
